import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { brl, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, Share2, Users, Gift, Wallet, TrendingUp, MessageCircle } from "lucide-react";

export default function Affiliate() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [ledger, setLedger] = useState([]);

  const load = async () => {
    const [s, r, l] = await Promise.all([
      api.get("/affiliate/me").catch(() => ({ data: null })),
      api.get("/affiliate/referrals").catch(() => ({ data: [] })),
      api.get("/affiliate/ledger").catch(() => ({ data: [] })),
    ]);
    setStats(s.data);
    setReferrals(r.data);
    setLedger(l.data);
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-400">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const refLink = stats ? `${window.location.origin}/register?ref=${stats.ref_code}` : "";

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  const shareWhatsapp = () => {
    const msg = `🎉 Entra pra Rifa RMC pelo meu link e concorra a prêmios incríveis por a partir de R$ 0,25! ${refLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-amber-200/80 mb-2">Programa de Afiliados</p>
        <h1 className="heading text-3xl sm:text-4xl font-extrabold text-slate-50">Indique e ganhe <span className="gold-gradient-text">crédito</span></h1>
        <p className="mt-3 text-slate-400 max-w-2xl">
          Compartilhe seu link e ganhe <span className="text-emerald-300 font-semibold">15% de crédito</span> na primeira compra de cada indicado. Use o crédito para comprar suas próprias cotas.
        </p>
      </div>

      {stats && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Kpi icon={<Wallet className="w-5 h-5" />} label="Crédito disponível" value={brl(stats.credit_balance)} highlight />
            <Kpi icon={<Users className="w-5 h-5" />} label="Indicados" value={stats.referrals_count} />
            <Kpi icon={<Gift className="w-5 h-5" />} label="Conversões" value={stats.converted_count} />
            <Kpi icon={<TrendingUp className="w-5 h-5" />} label="Total ganho" value={brl(stats.total_earned)} />
          </div>

          <div className="card-luxury rounded-3xl p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest text-amber-200 mb-2">Seu código de indicação</p>
                <div className="flex items-center gap-3">
                  <span className="heading text-3xl font-extrabold gold-gradient-text font-mono">{stats.ref_code}</span>
                  <Button size="sm" variant="outline" onClick={() => copy(stats.ref_code, "Código")} data-testid="copy-ref-code" className="border-white/10 bg-transparent text-slate-100">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex-1 min-w-[280px]">
                <p className="text-xs uppercase tracking-widest text-amber-200 mb-2">Seu link único</p>
                <div className="flex items-center gap-2">
                  <Input value={refLink} readOnly className="bg-slate-950 border-white/10 text-slate-100 font-mono text-xs" data-testid="ref-link-input" />
                  <Button size="icon" variant="outline" onClick={() => copy(refLink, "Link")} data-testid="copy-ref-link" className="border-white/10 bg-transparent text-slate-100 flex-shrink-0">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button onClick={shareWhatsapp} className="emerald-btn rounded-full" size="sm" data-testid="share-whatsapp">
                    <MessageCircle className="w-4 h-4 mr-2" /> Compartilhar no WhatsApp
                  </Button>
                  <Button variant="outline" className="border-white/10 bg-transparent text-slate-100 rounded-full" size="sm" onClick={() => { if (navigator.share) navigator.share({ url: refLink, title: "Rifa RMC" }); else copy(refLink, "Link"); }}>
                    <Share2 className="w-4 h-4 mr-2" /> Compartilhar
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <Tabs defaultValue="indicados">
            <TabsList className="bg-slate-900/50 border border-white/5">
              <TabsTrigger value="indicados" data-testid="tab-indicados" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Meus indicados</TabsTrigger>
              <TabsTrigger value="extrato" data-testid="tab-extrato" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Extrato de créditos</TabsTrigger>
              <TabsTrigger value="regras" data-testid="tab-regras" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Como funciona</TabsTrigger>
            </TabsList>

            <TabsContent value="indicados" className="mt-6">
              {referrals.length === 0 ? (
                <Empty text="Ainda ninguém se cadastrou com seu link. Compartilhe e comece a ganhar!" />
              ) : (
                <div className="space-y-3">
                  {referrals.map((r) => (
                    <div key={r.user_id} className="card-luxury rounded-2xl p-5 flex items-center justify-between">
                      <div>
                        <p className="text-slate-100 font-semibold">{r.name}</p>
                        <p className="text-xs text-slate-400">Cadastrado em {fmtDate(r.created_at)}</p>
                      </div>
                      <Badge className={r.converted ? "bg-emerald-500/15 border border-emerald-400/30 text-emerald-200" : "bg-slate-800 border border-white/10 text-slate-300"}>
                        {r.converted ? "✓ Convertido" : "Aguardando 1ª compra"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="extrato" className="mt-6">
              {ledger.length === 0 ? (
                <Empty text="Nenhuma movimentação de crédito ainda." />
              ) : (
                <div className="space-y-2">
                  {ledger.map((l) => (
                    <div key={l.ledger_id} className="card-luxury rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <p className="text-slate-100 text-sm">{l.reason}</p>
                        <p className="text-xs text-slate-500">{fmtDate(l.created_at)}</p>
                      </div>
                      <span className={`heading font-bold ${l.amount > 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {l.amount > 0 ? "+" : ""}{brl(l.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="regras" className="mt-6">
              <div className="card-luxury rounded-2xl p-6 space-y-4 text-slate-300 text-sm leading-relaxed">
                <Rule n="1" title="Compartilhe seu link" desc="Copie seu link único e envie no WhatsApp, Instagram ou onde preferir." />
                <Rule n="2" title="Alguém se cadastra" desc="Quando a pessoa criar a conta usando seu link, ela fica vinculada como sua indicação por 30 dias." />
                <Rule n="3" title="Ela faz a primeira compra" desc="No momento em que a pessoa confirmar a primeira compra de cotas, você recebe automaticamente 15% do valor em crédito na sua conta." />
                <Rule n="4" title="Use o crédito" desc="No checkout de qualquer rifa, marque 'Usar meu crédito' e o valor é abatido do total. O que sobrar você paga em PIX." />
                <p className="text-xs text-slate-500 pt-3 border-t border-white/5">
                  * A comissão é válida apenas para a primeira compra de cada indicado. O crédito não expira e não pode ser sacado.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

const Kpi = ({ icon, label, value, highlight }) => (
  <div className={`card-luxury rounded-2xl p-5 ${highlight ? "border-amber-500/40" : ""}`}>
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${highlight ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-amber-500/10 border border-amber-500/30 text-amber-200"}`}>
      {icon}
    </div>
    <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
    <div className={`heading text-2xl font-bold mt-1 ${highlight ? "gold-gradient-text" : "text-slate-50"}`}>{value}</div>
  </div>
);

const Empty = ({ text }) => (
  <div className="card-luxury rounded-2xl p-10 text-center text-slate-400">{text}</div>
);

const Rule = ({ n, title, desc }) => (
  <div className="flex gap-4">
    <span className="w-8 h-8 rounded-full gold-btn flex items-center justify-center text-sm font-bold flex-shrink-0">{n}</span>
    <div>
      <p className="text-slate-100 font-semibold">{title}</p>
      <p className="text-slate-400 text-sm">{desc}</p>
    </div>
  </div>
);
